import React, { useRef } from "react";
import { Form, Button, Label } from "semantic-ui-react";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import { useAuth } from "../../../../hooks";
import { useLanguage } from "../../../../contexts";
import { EnvironmentalRiskMatrixLeopoldform } from "../../../../api";
import { PROJECT_ACTIONS, ENVIRONMENTAL_FACTORS } from "../eiaCatalogs";
import { initialValues, validationSchema } from "./LeopoldForm.form";
import "../EnvironmentalRiskMatrixForm/EnvironmentalRiskMatrixForm.scss";

const environmentalRiskMatrixLeopoldCtrl = new EnvironmentalRiskMatrixLeopoldform();

function toOptions(list) {
  return list.map((v) => ({ key: v, value: v, text: v }));
}

// Leopold 1971: magnitud (-10..+10, sin 0) — extremos con etiqueta especial,
// igual que el prototipo.
const MAGNITUDE_OPTIONS = [
  ...[-10, -9, -8, -7, -6, -5, -4, -3, -2, -1].map((n) => ({
    key: n, value: n, text: n === -10 ? "−10" : n === -1 ? "−1" : String(n),
  })),
  ...[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => ({
    key: n, value: n, text: `+${n}`,
  })),
];
const IMPORTANCE_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => ({ key: n, value: n, text: String(n) }));

export function LeopoldForm(props) {
  const { onClose, onReload, item, siteSelected } = props;

  const { accessToken, user } = useAuth();
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  const submittingRef = useRef(false);

  const formik = useFormik({
    initialValues: initialValues(item),
    validationSchema: validationSchema(t),
    validateOnChange: false,
    onSubmit: async (formValue) => {
      if (submittingRef.current) return;
      submittingRef.current = true;
      try {
        if (!item) {
          const res = await environmentalRiskMatrixLeopoldCtrl.createEnvironmentalRiskMatrixLeopold(accessToken, {
            ...formValue,
            site: user?.site || siteSelected,
          });
          if (res.code === 200) toast.success(t("form_saved_successfully"));
        } else {
          const res = await environmentalRiskMatrixLeopoldCtrl.updateEnvironmentalRiskMatrixLeopold(accessToken, item._id, formValue);
          if (res.code === 200) toast.success(t("form_saved_successfully"));
        }
        setTimeout(() => {
          onReload?.();
          onClose?.();
        }, 800);
      } catch (error) {
        toast.error(error?.msg || error?.message || t("server_connection_error"));
      } finally {
        submittingRef.current = false;
      }
    },
  });

  const isPriority = (formik.values.importance || 0) >= 7;

  return (
    <Form className="risk-matrix-form" onSubmit={formik.handleSubmit}>
      <h3 className="risk-matrix-form__title">{item ? t("risk_matrix_edit_item") : t("risk_matrix_new_item")}</h3>
      <Form.Field error={formik.touched.title && !!formik.errors.title}>
        <label>{t("title")}</label>
        <Form.Input
          name="title"
          placeholder={t("risk_matrix_title_placeholder")}
          value={formik.values.title}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
        />
        {formik.touched.title && formik.errors.title && (
          <Label basic color="red" pointing>{formik.errors.title}</Label>
        )}
      </Form.Field>

      <Form.Field>
        <label>{t("description")}</label>
        <Form.TextArea name="description" value={formik.values.description} onChange={formik.handleChange} />
      </Form.Field>

      <div className="risk-matrix-form__row">
        <Form.Field error={formik.touched.projectAction && !!formik.errors.projectAction}>
          <label>{t("risk_matrix_eia_project_action")}</label>
          <Form.Dropdown
            selection
            search
            allowAdditions
            options={toOptions(PROJECT_ACTIONS)}
            value={formik.values.projectAction}
            placeholder={t("risk_matrix_eia_project_action_placeholder")}
            onAddItem={(_, { value }) => PROJECT_ACTIONS.push(value)}
            onChange={(_, { value }) => formik.setFieldValue("projectAction", value)}
            onBlur={() => formik.setFieldTouched("projectAction", true)}
          />
          {formik.touched.projectAction && formik.errors.projectAction && (
            <Label basic color="red" pointing>{formik.errors.projectAction}</Label>
          )}
        </Form.Field>

        <Form.Field error={formik.touched.environmentalFactor && !!formik.errors.environmentalFactor}>
          <label>{t("risk_matrix_eia_environmental_factor")}</label>
          <Form.Dropdown
            selection
            search
            allowAdditions
            options={toOptions(ENVIRONMENTAL_FACTORS)}
            value={formik.values.environmentalFactor}
            placeholder={t("risk_matrix_eia_environmental_factor_placeholder")}
            onAddItem={(_, { value }) => ENVIRONMENTAL_FACTORS.push(value)}
            onChange={(_, { value }) => formik.setFieldValue("environmentalFactor", value)}
            onBlur={() => formik.setFieldTouched("environmentalFactor", true)}
          />
          {formik.touched.environmentalFactor && formik.errors.environmentalFactor && (
            <Label basic color="red" pointing>{formik.errors.environmentalFactor}</Label>
          )}
        </Form.Field>
      </div>

      <div className="risk-matrix-form__row">
        <Form.Field error={formik.touched.magnitude && !!formik.errors.magnitude}>
          <label>{t("risk_matrix_leopold_magnitude")}</label>
          <Form.Select
            options={MAGNITUDE_OPTIONS}
            placeholder="-10..+10"
            value={formik.values.magnitude}
            onChange={(_, { value }) => formik.setFieldValue("magnitude", value)}
            onBlur={() => formik.setFieldTouched("magnitude", true)}
          />
          {formik.touched.magnitude && formik.errors.magnitude && (
            <Label basic color="red" pointing>{formik.errors.magnitude}</Label>
          )}
        </Form.Field>

        <Form.Field error={formik.touched.importance && !!formik.errors.importance}>
          <label>{t("risk_matrix_leopold_importance")}</label>
          <Form.Select
            options={IMPORTANCE_OPTIONS}
            placeholder="1-10"
            value={formik.values.importance}
            onChange={(_, { value }) => formik.setFieldValue("importance", value)}
            onBlur={() => formik.setFieldTouched("importance", true)}
          />
          {formik.touched.importance && formik.errors.importance && (
            <Label basic color="red" pointing>{formik.errors.importance}</Label>
          )}
        </Form.Field>
      </div>

      {formik.values.importance && (
        <div className="risk-matrix-form__preview">
          <span className={`risk-matrix-form__preview-sig${isPriority ? " is-significant" : ""}`}>
            {isPriority ? t("risk_matrix_leopold_priority_yes") : t("risk_matrix_leopold_priority_no")}
          </span>
        </div>
      )}

      <Form.Field>
        <label>{t("risk_matrix_controls")}</label>
        <Form.TextArea name="controls" value={formik.values.controls} onChange={formik.handleChange} />
      </Form.Field>

      <Form.Field>
        <label>{t("risk_matrix_action")}</label>
        <Form.TextArea name="action" value={formik.values.action} onChange={formik.handleChange} />
      </Form.Field>

      <div className="risk-matrix-form__actions">
        <Button type="submit" primary loading={formik.isSubmitting} disabled={formik.isSubmitting}>
          {!item ? t("save") : t("update")}
        </Button>
        <Button type="button" onClick={() => onClose?.()}>
          {t("cancel")}
        </Button>
      </div>
    </Form>
  );
}
